import React, { FC } from 'react';
import { BasketRentPaymentSelectionProps } from '@components/v2/BasketRentPaymentSelection';
import useTranslations from '@hooks/useTranslations';
import { BUDGET_TYPE_RENT } from '../../../services';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useRouter } from 'next/router';
import { Link as LinkV2 } from '@components/v2/link';
import Link from '@components/Link';
import { isBrandAC, LANGUAGE, LEASYS_PROVIDER_TYPE, MARKET, ModalVersion } from '../../../constants/main';
import { useDispatch, useSelector } from 'react-redux';
import FinanceWidgetDuck from '../../../redux/financeWidget/financeWidget.duck';
import PriceSuffix from '@components/PriceSuffix';
import FinanceCalculatorService from '../../../services/financeQuote/financePrices.service';
import { localeToCurrency } from '@utils/localeToCurrency';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { ICalculateSummaryRequestData } from '../../../services/calculateSummary/calculateSummary.types';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { usePrices } from '@hooks/usePriceCalculator';
import { useFinanceWidgetLogic } from '@hooks/useFinanceWidgetLogic';
import {
    getCatalogOptionOrColorPrice,
    getCatalogOptionOrColorPriceWithoutTax,
    getMonthlyPriceObjByType,
    getTotalOptionOrColorPrice,
    getTotalOptionOrColorPriceWithoutTax,
} from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';
import { useJourneyType } from '@hooks/useJourneyType';
import { formatColorToAccessory } from '@utils/FinanceWidget.utils';
import { Redux } from '../../../redux/redux.interface';
import ConfiguratorDuck from '../../../redux/configurator/configurator.duck';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { IOption } from '../../../services/stock/models/stockItems.service.model';
import { IConfiguratorOption } from '../../../redux/configurator/configurator.duck.interface';
import { LEASYS_BRAND } from '../../../constants/gtm';
import { FINANCE } from '../../../services/filters/filters';
import UIDuck from '../../../redux/commonDucks/ui.duck';

const BasketRentPaymentSelectionTemplate: FC<BasketRentPaymentSelectionProps> = ({
    isSelected,
    onClick,
    className,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const dispatch = useDispatch();

    const { isStockJourney } = useCarStockJourneyCheck();

    const { isRent } = useJourneyType();

    const { getClientType, getOptions } = useFinanceWidgetLogic(0);

    const { carOptions } = useConfiguratorDuck();

    const { formatMoney } = useMoneyFormatter();

    const { currentDeal, carDetails } = useCarDetailsDuck();

    const { catalog, cash } = usePrices(carDetails, true, null, true);

    const currentFinanceQuote = useSelector(FinanceWidgetDuck.getLeasysQuote);

    const configurator = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state));

    const carConfigId = carDetails?.externalId || carDetails?._id;
    const params: ICalculateSummaryRequestData = {
        carConfigId,
        token: isRent ? currentFinanceQuote?.token : null,
        carConfiguration: carDetails,
        ...(currentDeal?.extraFields?.quotationId ? { quotationId: currentDeal?.extraFields?.quotationId } : null),
    };

    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params, LEASYS_PROVIDER_TYPE);

    return (
        <div className={className}>
            <button
                className={`section ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                    onClick(BUDGET_TYPE_RENT);
                }}
                data-testid="TESTING_SELECTION_RENT"
            >
                <div className="inner">
                    <span className="title">
                        <span className={`icon ${isSelected ? 'selected' : ''}`}>
                            <span className={isSelected ? 'selected' : ''} />
                        </span>{' '}
                        {t('basket.payment.rent.title')}
                    </span>
                    <span className="description">{t('basket.payment.rent.description')}</span>
                    <div className="summary">
                        <span className="totalPrice">
                            {formatMoney(currentFinanceQuote?.monthlyPayment, false, '', amountRounding)}{' '}
                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                        </span>
                        <span className="primaryText">{currentFinanceQuote?.primaryText?.[0]?.text}</span>
                        {isBrandAC ? (
                            <LinkV2
                                txt={t('summary.financeDetail')}
                                onClick={(event: Event) => {
                                    event.stopPropagation();
                                    processCalculateSummaryDataPromise();
                                }}
                                isUnderline
                                style={{ marginTop: '-10px' }}
                            />
                        ) : (
                            <Link
                                label={t('summary.financeDetail')}
                                onClick={(event: Event) => {
                                    event.stopPropagation();
                                    processCalculateSummaryDataPromise();
                                }}
                                marginBottom={20}
                                secondary
                            />
                        )}
                        {isSelected && (
                            <>
                                <div className="otherPrices">
                                    {currentFinanceQuote?.depositWithVat > 0 && (
                                        <div className="otherPrice">
                                            <span>{t('finance.firstRental')}</span>
                                            <span>
                                                <strong>{formatMoney(currentFinanceQuote?.depositWithVat)}</strong>
                                            </span>
                                        </div>
                                    )}
                                    <div className="otherPrice">
                                        <span>{t('finance.duration')}</span>
                                        <span>
                                            <strong>
                                                {currentFinanceQuote?.duration} {t('finance.duration.unit')}
                                            </strong>
                                        </span>
                                    </div>
                                    <div className="otherPrice">
                                        <span>{t('finance.annualMileage')}</span>
                                        <span>
                                            <strong>
                                                {currentFinanceQuote?.annualMileage} {t('finance.annualMileage.units')}
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                                <p
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: t('rent.legal.text.long') }}
                                />
                                <Link
                                    label={t('basket.payment.rent.personalize')}
                                    onClick={() =>
                                        // @ts-ignore
                                        showModalWidget({
                                            context: {
                                                quotationID: currentFinanceQuote?.quotationId || '',
                                                countryCode: MARKET.toUpperCase(),
                                                languageCode: LANGUAGE,
                                                siteCode: 'SOL',
                                                brandName: LEASYS_BRAND,
                                                componentCode: 'Widget',
                                                customer: {
                                                    clientType: 'E',
                                                },
                                            },
                                            vehicle: {
                                                manufacturerCode: carDetails?.lcdv16,
                                                pricing: {
                                                    basicPriceExclTax: catalog?.totalCashPriceExclTax,
                                                    basicPriceInclTax: catalog?.totalCashPriceInclTax,
                                                    netPriceExclTax: cash?.leasys?.netPriceExclTax,
                                                    netPriceInclTax: cash?.leasys?.netPriceInclTax,
                                                },
                                                features: {
                                                    kind: 'VP',
                                                },
                                                options: [
                                                    ...carDetails?.options?.map((option: IOption) => ({
                                                        code: option.id,
                                                        label: option?.title,
                                                        pricing: {
                                                            basicPriceInclTax: getCatalogOptionOrColorPrice(option),
                                                            netPriceInclTax: getTotalOptionOrColorPrice(
                                                                option,
                                                                FINANCE
                                                            ),
                                                            basicPriceExclTax:
                                                                getCatalogOptionOrColorPriceWithoutTax(option),
                                                            netPriceExclTax: getTotalOptionOrColorPriceWithoutTax(
                                                                option,
                                                                FINANCE
                                                            ),
                                                        },
                                                    })),
                                                    ...[
                                                        formatColorToAccessory(carDetails.interiorColour, true, true),
                                                        formatColorToAccessory(carDetails.exteriorColour, true, true),
                                                    ],
                                                ],
                                            },
                                            outputFn: async (output: any) => {
                                                console.log('OUTPUT', output);

                                                try {
                                                    const { data } = await FinanceCalculatorService.updateFinanceQuote(
                                                        {
                                                            carConfigId: carDetails?.externalId,
                                                            financeProductKey: output?.result?.teaser?.data?.productKey,
                                                            annualMileage: output?.result?.teaser?.data?.annualMileage,
                                                            duration: output?.result?.teaser?.data?.duration,
                                                            currency: localeToCurrency(LANGUAGE),
                                                            deposit: output?.result?.teaser?.data?.deposit,
                                                            isPersonalized: true,
                                                            providerType: LEASYS_PROVIDER_TYPE,
                                                            quotationId: output?.result?.quotationID,
                                                            globalMileage: output?.result?.teaser?.data?.globalMileage,
                                                        },
                                                        currentDeal.financeQuoteId
                                                    );
                                                    dispatch(
                                                        FinanceWidgetDuck.setLeasysQuote({
                                                            ...data,
                                                        })
                                                    );
                                                } catch {
                                                    dispatch(
                                                        UIDuck.openModal({
                                                            modalType: UIDuck.MODAL_TYPES.MISSING_PARTS,
                                                            data: {
                                                                title: t('modal.defaultError.title'),
                                                                message: t('modal.defaultError.description'),
                                                                buttonText: t('modal.defaultError.button'),
                                                            },
                                                            modalVersion: ModalVersion.v2,
                                                            modalProperties: {
                                                                centerModeOnDesktop: true,
                                                            },
                                                        })
                                                    );
                                                }
                                            },
                                            ...(currentFinanceQuote.isPersonalized && {
                                                parameters: {
                                                    cashDeposit: currentFinanceQuote?.deposit,
                                                    duration: currentFinanceQuote?.duration,
                                                    globalMileage: currentFinanceQuote?.globalMileage,
                                                    productKey: currentFinanceQuote?.financeProductKey,
                                                },
                                            }),
                                        })
                                    }
                                    withArrowIcon
                                    primary={!isBrandAC}
                                    secondary={isBrandAC}
                                    marginBottom={20}
                                />
                            </>
                        )}
                    </div>
                </div>
            </button>
        </div>
    );
};

export default BasketRentPaymentSelectionTemplate;
